import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { RootState } from '@/redux/store';

export const DEFAULT_AI_ASSISTANT_INITIAL_MESSAGE = "Hi, I'm PLI's AI assistant. How can I help?";

export type AIAssistantMessageType = 'system' | 'you' | 'assistant';
export type AIAssistantEndpoint = '/api/chat/stream' | '/api/chat/stream/apply-filters';
export type AIAssistantFeedbackSelection = 'up' | 'down';

export interface AIAssistantDebugEntry {
  id: string;
  type: 'info' | 'model';
  label: string;
  value: string;
  timestamp: string;
  contentType?: 'string' | 'number' | 'boolean' | 'null' | 'array' | 'object';
  contentSize?: number;
  isTruncated?: boolean;
}

export interface AIAssistantKeyValuePair {
  key?: string;
  value?: string;
  Key?: string;
  Value?: string;
}

export interface AIAssistantFacetInfo {
  group: string;
  name: string;
  value: string;
  checked: boolean;
}

export interface AIAssistantMessage {
  id: string;
  type: AIAssistantMessageType;
  text: string;
  timestamp: string;
  messageId?: string;
  appliedFilters?: AIAssistantKeyValuePair[];
  suggestedFilters?: AIAssistantKeyValuePair[];
  promptSuggestions: string[];
  debugEntries?: AIAssistantDebugEntry[];
  isStreaming?: boolean;
}

export interface AIAssistantState {
  assistantUrl: string;
  initialMessage: string;
  chatId: string;
  messages: AIAssistantMessage[];
  input: string;
  info: string;
  isThinking: boolean;
  error: string;
  suggestedFilters: AIAssistantKeyValuePair[];
  promptSuggestions: string[];
  feedbackSelections: Record<string, AIAssistantFeedbackSelection>;
}

interface AIAssistantRequest {
  message: string;
  chatId: string;
  isPersonalized: boolean;
  messageId: string;
}

interface AIAssistantStreamEvent {
  eventType: string;
  data: string;
}

interface InitializeAIAssistantPayload {
  assistantUrl: string;
  initialMessage?: string;
}

interface SendAIAssistantMessageArgs {
  text: string;
  endpoint?: AIAssistantEndpoint;
}

interface SubmitAIAssistantFeedbackArgs {
  messageId: string;
  isThumbsUp: boolean;
}

interface BeginAssistantMessagePayload {
  id: string;
  messageId: string;
}

interface AssistantContentPayload {
  id: string;
  text: string;
}

interface ModelEventPayload {
  id: string;
  modelType: string;
  content: unknown;
}

interface FeedbackSelectionPayload {
  messageId: string;
  feedback: AIAssistantFeedbackSelection;
}

const streamContentType = 'text/event-stream';
const MAX_DEBUG_ENTRY_VALUE_LENGTH = 2000;

function newId(): string {
  if (globalThis.crypto && typeof globalThis.crypto.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function normalizeBaseUrl(baseUrl?: string): string {
  return (baseUrl || '').replace(/\/+$/, '');
}

function parseJson(data: string): unknown {
  if (data === undefined || data === null || data === '') {
    return null;
  }

  try {
    return JSON.parse(data);
  } catch {
    return data;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function toOptionalString(value: unknown): string | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }

  return String(value);
}

function toText(value: unknown): string {
  if (value === undefined || value === null) {
    return '';
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function getDebugContentType(
  value: unknown
): 'string' | 'number' | 'boolean' | 'null' | 'array' | 'object' {
  if (value === null || value === undefined) {
    return 'null';
  }

  if (Array.isArray(value)) {
    return 'array';
  }

  if (typeof value === 'string') {
    return 'string';
  }

  if (typeof value === 'number') {
    return 'number';
  }

  if (typeof value === 'boolean') {
    return 'boolean';
  }

  return 'object';
}

function createDebugEntryValue(
  content: unknown
): Pick<AIAssistantDebugEntry, 'value' | 'contentType' | 'contentSize' | 'isTruncated'> {
  const serializedContent = toText(content);
  const isTruncated = serializedContent.length > MAX_DEBUG_ENTRY_VALUE_LENGTH;

  return {
    value: isTruncated
      ? `${serializedContent.slice(0, MAX_DEBUG_ENTRY_VALUE_LENGTH - 3)}...`
      : serializedContent,
    contentType: getDebugContentType(content),
    contentSize: serializedContent.length,
    isTruncated
  };
}

function toKeyValuePairs(value: unknown): AIAssistantKeyValuePair[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isRecord).map(item => ({
    key: toOptionalString(item.key),
    value: toOptionalString(item.value),
    Key: toOptionalString(item.Key),
    Value: toOptionalString(item.Value)
  }));
}

function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.map(toText).filter(Boolean);
}

function parseEvent(rawEvent: string): AIAssistantStreamEvent {
  const dataLines: string[] = [];
  let eventType = '';

  for (const line of rawEvent.split(/\r?\n/)) {
    if (line.startsWith('event:')) {
      eventType = line.slice(6).trim();
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trim());
    }
  }

  return {
    eventType,
    data: dataLines.join('\n')
  };
}

async function readEventStream(
  response: Response,
  onEvent: (event: AIAssistantStreamEvent) => Promise<boolean>
): Promise<void> {
  if (!response.body) {
    throw new Error('Streaming response did not include a body.');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let isReading = true;

  while (isReading) {
    const { done, value } = await reader.read();

    if (done) {
      isReading = false;
      break;
    }

    buffer += decoder.decode(value, { stream: true });
    const events = buffer.split('\n\n');
    buffer = events.pop() || '';

    for (const rawEvent of events) {
      if (!rawEvent.trim()) {
        continue;
      }

      const shouldContinue = await onEvent(parseEvent(rawEvent));

      if (!shouldContinue) {
        await reader.cancel();
        return;
      }
    }
  }

  if (buffer.trim()) {
    await onEvent(parseEvent(buffer));
  }
}

async function postStream(
  assistantUrl: string,
  path: AIAssistantEndpoint,
  payload: AIAssistantRequest,
  token: string | null | undefined,
  onEvent: (event: AIAssistantStreamEvent) => Promise<boolean>
): Promise<void> {
  const headers: Record<string, string> = {
    Accept: streamContentType,
    'Content-Type': 'application/json'
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${normalizeBaseUrl(assistantUrl)}${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
    credentials: 'include'
  });

  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }

  await readEventStream(response, onEvent);
}

async function postFeedback(
  assistantUrl: string,
  messageId: string,
  isThumbsUp: boolean,
  token: string | null | undefined
): Promise<void> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${normalizeBaseUrl(assistantUrl)}/api/chat/feedback`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ messageId, isThumbsUp }),
    credentials: 'include'
  });

  if (!response.ok) {
    throw new Error(`Feedback request failed with status ${response.status}`);
  }
}

function createInitialMessage(initialMessage: string): AIAssistantMessage[] {
  if (!initialMessage.trim()) {
    return [];
  }

  return [
    {
      id: newId(),
      type: 'system',
      text: initialMessage,
      timestamp: new Date().toISOString(),
      promptSuggestions: []
    }
  ];
}

function createInitialState({
  assistantUrl = '',
  initialMessage = ''
}: Partial<InitializeAIAssistantPayload> = {}): AIAssistantState {
  return {
    assistantUrl,
    initialMessage,
    chatId: newId(),
    messages: createInitialMessage(initialMessage),
    input: '',
    info: '',
    isThinking: false,
    error: '',
    suggestedFilters: [],
    promptSuggestions: [],
    feedbackSelections: {}
  };
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export function getAIAssistantPairKey(pair?: AIAssistantKeyValuePair): string {
  return pair?.key ?? pair?.Key ?? '';
}

export function getAIAssistantPairValue(pair?: AIAssistantKeyValuePair): string {
  return pair?.value ?? pair?.Value ?? '';
}

export function parseAIAssistantFacet(facet: string): AIAssistantFacetInfo | null {
  const parts = String(facet || '').split('|');

  if (parts.length < 3) {
    return null;
  }

  const groupAndName = parts[0].split(':');

  if (groupAndName.length !== 2) {
    return null;
  }

  return {
    group: groupAndName[0],
    name: groupAndName[1],
    value: parts[1],
    checked: String(parts[2]).toLowerCase() === 'true'
  };
}

const initialState = createInitialState();

const aiAssistantSlice = createSlice({
  name: 'aiAssistant',
  initialState,
  reducers: {
    initializeAIAssistant: (_state, action: PayloadAction<InitializeAIAssistantPayload>) =>
      createInitialState({
        assistantUrl: action.payload.assistantUrl,
        initialMessage: action.payload.initialMessage ?? DEFAULT_AI_ASSISTANT_INITIAL_MESSAGE
      }),
    setAIAssistantInput: (state, action: PayloadAction<string>) => {
      state.input = action.payload;
    },
    addAIAssistantUserMessage: (state, action: PayloadAction<{ text: string }>) => {
      state.messages.push({
        id: newId(),
        type: 'you',
        text: action.payload.text,
        timestamp: new Date().toISOString(),
        promptSuggestions: []
      });
      state.input = '';
      state.error = '';
      state.promptSuggestions = [];
      state.suggestedFilters = [];
    },
    beginAIAssistantMessage: (state, action: PayloadAction<BeginAssistantMessagePayload>) => {
      state.isThinking = true;
      state.info = 'Initializing';
      state.error = '';
      state.messages.push({
        id: action.payload.id,
        type: 'assistant',
        text: '',
        timestamp: new Date().toISOString(),
        messageId: action.payload.messageId,
        appliedFilters: [],
        suggestedFilters: [],
        promptSuggestions: [],
        debugEntries: [],
        isStreaming: true
      });
    },
    appendAIAssistantContent: (state, action: PayloadAction<AssistantContentPayload>) => {
      const message = state.messages.find(item => item.id === action.payload.id);

      if (!message) {
        return;
      }

      message.text += action.payload.text;
      message.timestamp = new Date().toISOString();
      state.info = '';
    },
    applyAIAssistantModelEvent: (state, action: PayloadAction<ModelEventPayload>) => {
      const message = state.messages.find(item => item.id === action.payload.id);

      if (!message) {
        return;
      }

      const debugEntryValue = createDebugEntryValue(action.payload.content);

      message.debugEntries ??= [];
      message.debugEntries.push({
        id: newId(),
        type: 'model',
        label: action.payload.modelType || 'Model',
        ...debugEntryValue,
        timestamp: new Date().toISOString()
      });

      if (action.payload.modelType === 'AppliedFilters') {
        message.appliedFilters = toKeyValuePairs(action.payload.content);
      } else if (action.payload.modelType === 'SuggestedFilters') {
        const suggestedFilters = toKeyValuePairs(action.payload.content);

        // Keep existing suggestions when the backend returns an empty set for apply-filter actions.
        if (suggestedFilters.length > 0) {
          message.suggestedFilters = suggestedFilters;
          state.suggestedFilters = suggestedFilters;
        }
      } else if (action.payload.modelType === 'PromptSuggestions') {
        message.promptSuggestions = toStringArray(action.payload.content);
        state.promptSuggestions = message.promptSuggestions;
      }

      message.timestamp = new Date().toISOString();
    },
    setAIAssistantInfo: (state, action: PayloadAction<AssistantContentPayload>) => {
      state.info = action.payload.text;
      const message = state.messages.find(item => item.id === action.payload.id);

      if (message) {
        message.debugEntries ??= [];
        message.debugEntries.push({
          id: newId(),
          type: 'info',
          label: 'Info',
          value: action.payload.text,
          timestamp: new Date().toISOString()
        });
      }
    },
    setAIAssistantError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    markAIAssistantError: (state, action: PayloadAction<AssistantContentPayload>) => {
      const message = state.messages.find(item => item.id === action.payload.id);

      if (message) {
        message.text = action.payload.text;
        message.timestamp = new Date().toISOString();
      }
    },
    finishAIAssistantThinking: state => {
      state.isThinking = false;
      state.info = '';
      state.messages.forEach(message => {
        if (message.type === 'assistant') {
          message.isStreaming = false;
        }
      });
    },
    startNewAIAssistantChat: state =>
      createInitialState({
        assistantUrl: state.assistantUrl,
        initialMessage: state.initialMessage
      }),
    setAIAssistantFeedbackSelection: (state, action: PayloadAction<FeedbackSelectionPayload>) => {
      state.feedbackSelections[action.payload.messageId] = action.payload.feedback;
    }
  }
});

export const sendAIAssistantMessage = createAsyncThunk<
  void,
  SendAIAssistantMessageArgs,
  { state: RootState }
>(
  'aiAssistant/sendMessage',
  async ({ text, endpoint = '/api/chat/stream' }, { dispatch, getState }) => {
    const messageText = String(text || '').trim();
    const state = getState().aiAssistant;

    if (!messageText || state.isThinking) {
      return;
    }

    const messageId = newId();
    const assistantId = newId();

    if (endpoint === '/api/chat/stream') {
      dispatch(aiAssistantSlice.actions.addAIAssistantUserMessage({ text: messageText }));
    }

    dispatch(
      aiAssistantSlice.actions.beginAIAssistantMessage({
        id: assistantId,
        messageId
      })
    );

    const request: AIAssistantRequest = {
      message: messageText,
      chatId: state.chatId,
      isPersonalized: false,
      messageId
    };

    try {
      await postStream(
        state.assistantUrl,
        endpoint,
        request,
        getState().auth.token,
        async event => {
          if (event.eventType === 'done') {
            return false;
          }

          if (event.eventType === 'content') {
            dispatch(
              aiAssistantSlice.actions.appendAIAssistantContent({
                id: assistantId,
                text: toText(parseJson(event.data))
              })
            );
          } else if (event.eventType === 'model') {
            const model = parseJson(event.data);

            if (isRecord(model)) {
              dispatch(
                aiAssistantSlice.actions.applyAIAssistantModelEvent({
                  id: assistantId,
                  modelType: toText(model.type ?? model.Type),
                  content: model.content ?? model.Content ?? []
                })
              );
            }
          } else if (event.eventType === 'info') {
            dispatch(
              aiAssistantSlice.actions.setAIAssistantInfo({
                id: assistantId,
                text: toText(parseJson(event.data))
              })
            );
          } else if (event.eventType === 'error') {
            const errorMessage =
              toText(parseJson(event.data)) || 'An error occurred while generating the response.';

            dispatch(
              aiAssistantSlice.actions.markAIAssistantError({
                id: assistantId,
                text: errorMessage
              })
            );
            dispatch(aiAssistantSlice.actions.setAIAssistantError(errorMessage));
            return false;
          }

          return true;
        }
      );
    } catch (error) {
      dispatch(
        aiAssistantSlice.actions.markAIAssistantError({
          id: assistantId,
          text: 'Oops! Something went wrong. Please try again.'
        })
      );
      dispatch(aiAssistantSlice.actions.setAIAssistantError(getErrorMessage(error)));
    } finally {
      dispatch(aiAssistantSlice.actions.finishAIAssistantThinking());
    }
  }
);

export const submitAIAssistantFeedback = createAsyncThunk<
  void,
  SubmitAIAssistantFeedbackArgs,
  { state: RootState }
>('aiAssistant/submitFeedback', async ({ messageId, isThumbsUp }, { dispatch, getState }) => {
  if (!messageId) {
    return;
  }

  dispatch(
    aiAssistantSlice.actions.setAIAssistantFeedbackSelection({
      messageId,
      feedback: isThumbsUp ? 'up' : 'down'
    })
  );

  try {
    const state = getState();
    await postFeedback(state.aiAssistant.assistantUrl, messageId, isThumbsUp, state.auth.token);
  } catch (error) {
    dispatch(aiAssistantSlice.actions.setAIAssistantError(getErrorMessage(error)));
  }
});

export const {
  initializeAIAssistant,
  setAIAssistantInput,
  addAIAssistantUserMessage,
  beginAIAssistantMessage,
  appendAIAssistantContent,
  applyAIAssistantModelEvent,
  setAIAssistantInfo,
  setAIAssistantError,
  markAIAssistantError,
  finishAIAssistantThinking,
  startNewAIAssistantChat,
  setAIAssistantFeedbackSelection
} = aiAssistantSlice.actions;

export default aiAssistantSlice.reducer;
